import type { MouseEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import type { Grant, RdsRegistration, Role } from "../api";
import { api } from "../api";

function grantKey(g: Grant) {
  return `${g.rds_id}\0${g.schema_name}`;
}

type Props = {
  token: string;
  role: Role;
  onClose: () => void;
  onSaved?: () => void;
};

export function RoleGrantsModal({ token, role, onClose, onSaved }: Props) {
  const [rds, setRds] = useState<RdsRegistration[] | null>(null);
  const [grants, setGrants] = useState<Grant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [addRdsId, setAddRdsId] = useState<number | "">("");
  const [addSchema, setAddSchema] = useState("");

  const rdsMap = useMemo(() => new Map(rds?.map((r) => [r.id, r]) ?? []), [rds]);

  useEffect(() => {
    setAddRdsId("");
    setAddSchema("");
  }, [role.id]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([api.listRds(token), api.getRoleGrants(token, role.id)])
      .then(([list, g]) => {
        if (!cancelled) {
          setRds(list);
          setGrants(g ?? []);
          if (list.length) setAddRdsId((cur) => (cur === "" ? list[0].id : cur));
        }
      })
      .catch((e: { message?: string }) => {
        if (!cancelled) setError(e?.message ?? String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, role.id]);

  const selectedRegistration = useMemo(() => {
    if (addRdsId === "") return undefined;
    return rds?.find((r) => r.id === addRdsId);
  }, [addRdsId, rds]);

  const availableSchemas = useMemo(() => {
    const reg = selectedRegistration;
    if (!reg) return [];
    const existing = new Set(grants.filter((g) => g.rds_id === reg.id).map((g) => g.schema_name));
    return reg.schemas.filter((s) => !existing.has(s)).sort((a, b) => a.localeCompare(b));
  }, [selectedRegistration, grants]);

  useEffect(() => {
    if (addSchema && !availableSchemas.includes(addSchema)) setAddSchema("");
  }, [addSchema, availableSchemas]);

  const onAddGrant = () => {
    if (addRdsId === "" || !addSchema.trim()) return;
    const schema_name = addSchema.trim();
    if (grants.some((g) => g.rds_id === addRdsId && g.schema_name === schema_name)) return;
    setGrants((prev) => [...prev, { rds_id: Number(addRdsId), schema_name }].sort(byRdsThenSchema));
  };

  const onRemoveGrant = (g: Grant) => {
    setGrants((prev) => prev.filter((x) => grantKey(x) !== grantKey(g)));
  };

  const onBackdrop = (ev: MouseEvent<HTMLDivElement>) => {
    if (ev.target === ev.currentTarget) onClose();
  };

  const onClearAll = () => setGrants([]);

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.putRoleGrants(token, role.id, grants);
      onSaved?.();
      onClose();
    } catch (e: unknown) {
      const message = typeof e === "object" && e && "message" in e ? String((e as { message?: string }).message) : String(e);
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onBackdrop}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg border border-slate-200 shadow-xl max-w-xl w-full max-h-[85vh] flex flex-col"
        role="dialog"
        aria-labelledby="role-grants-modal-title"
        onMouseDown={(ev) => ev.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-slate-200 flex justify-between gap-4">
          <div>
            <h3 id="role-grants-modal-title" className="text-base font-semibold text-slate-900">
              Schema grants
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Role <span className="font-mono">{role.name}</span> (id {role.id}). Members of this role get these schemas.
            </p>
          </div>
          <button type="button" onClick={onClose} className="btn shrink-0" aria-label="Close">
            Close
          </button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto space-y-4">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
          )}

          {loading || rds === null ? (
            <p className="text-sm text-slate-500">Loading databases and grants…</p>
          ) : rds.length === 0 ? (
            <p className="text-sm text-slate-600">
              No RDS registrations yet. Register a database first (Database management → Register RDS).
            </p>
          ) : (
            <>
              <div className="space-y-2">
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">Add grant</label>
                <div className="flex flex-wrap gap-2 items-end">
                  <div className="flex-1 min-w-[140px]">
                    <label className="block text-[11px] text-slate-500 mb-1">Database</label>
                    <select
                      className="input"
                      value={addRdsId === "" ? "" : String(addRdsId)}
                      onChange={(e) => setAddRdsId(e.target.value ? Number(e.target.value) : "")}
                    >
                      <option value="">Select…</option>
                      {rds.map((r) => (
                        <option key={r.id} value={r.id}>
                          #{r.id} · {r.display_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1 min-w-[140px]">
                    <label className="block text-[11px] text-slate-500 mb-1">Schema</label>
                    <select className="input" value={addSchema} onChange={(e) => setAddSchema(e.target.value)}>
                      <option value="">
                        {!selectedRegistration
                          ? "—"
                          : availableSchemas.length
                            ? "Select schema…"
                            : "(all schemas granted)"}
                      </option>
                      {availableSchemas.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button type="button" className="btn-primary" disabled={addRdsId === "" || !addSchema} onClick={onAddGrant}>
                    Add
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  Only schemas discovered on the RDS registration are listed. Refresh schemas after new databases are created.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Grants on this role ({grants.length})
                  </label>
                  <button type="button" className="btn text-xs py-1" disabled={!grants.length} onClick={onClearAll}>
                    Clear all
                  </button>
                </div>
                {grants.length === 0 ? (
                  <p className="text-sm text-slate-500 italic">No grants configured.</p>
                ) : (
                  <table className="table-clean rounded border border-slate-200">
                    <thead>
                      <tr>
                        <th>rds_id</th>
                        <th>database</th>
                        <th>schema</th>
                        <th className="text-right"> </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...grants].sort(byRdsThenSchema).map((g) => {
                        const rr = rdsMap.get(g.rds_id);
                        return (
                          <tr key={grantKey(g)} className="border-t border-slate-100">
                            <td className="font-mono">{g.rds_id}</td>
                            <td className="max-w-[12rem] truncate" title={rr?.display_name ?? ""}>
                              {rr?.display_name ?? "—"}
                            </td>
                            <td className="font-mono text-xs">{g.schema_name}</td>
                            <td className="text-right">
                              <button type="button" className="btn text-xs py-1" onClick={() => onRemoveGrant(g)}>
                                remove
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2 bg-slate-50 rounded-b-lg">
          <button type="button" className="btn" disabled={saving} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" disabled={saving || loading || !rds} onClick={() => void onSave()}>
            {saving ? "Saving…" : "Save grants"}
          </button>
        </div>
      </div>
    </div>
  );
}

function byRdsThenSchema(a: Grant, b: Grant) {
  if (a.rds_id !== b.rds_id) return a.rds_id - b.rds_id;
  return a.schema_name.localeCompare(b.schema_name);
}
